import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { LogOutIcon, PlusIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import type { MessageThreadWithParticipants } from "simplydo/interfaces/types/messageTypes";
import { css, cx, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import { Modal, ModalContent } from "simplydo/react/ui/modal";
import { Segment } from "simplydo/react/ui/segment";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import type { MessageInputChangeEvent } from "./types";

const modalContainerClass = css`
  display: flex;

  &.is-margined-top {
    margin-top: 12.5px;
  }
`;

const ParticipantContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActionArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Participant = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

type ChatSettingsProps = {
  thread: MessageThreadWithParticipants;
  modalOpen: boolean;
  onCloseModal: () => void;
  closeThread: (threadId: string) => void;
  createThread: (
    onCreate?: (thread: OpenAPI.Schemas["MessageThread"]) => void,
    onFail?: () => void,
  ) => void;
  isNewThread: boolean;
};

const ChatSettings = ({
  thread,
  modalOpen,
  onCloseModal,
  closeThread,
  createThread,
  isNewThread,
}: ChatSettingsProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const onMessageThreadSelected = useCallback(
    (thread: OpenAPI.Schemas["MessageThread"] | null) => {
      dispatch(actions.messages.selectThread(thread));
    },
    [dispatch],
  );

  const [searchState, setSearchState] = useState({
    results: [] as OpenAPI.Schemas["User"][],
    searching: false,
    searchValue: "",
  });
  const { results } = searchState;
  const {
    participantUsers = [],
    participants = [],
    participantsMuted = [],
  } = thread || {};
  const threadId = thread?._id ?? "";
  const isMuted = !!user?._id && participantsMuted.indexOf(user._id) > -1;

  const addParticipant = useCallback(
    (newId: string) => {
      util
        .confirm(
          "Add participant",
          "Adding a participant will allow them to view and reply to any message in this chat. This includes messages that were sent before this user was added. Are you sure you want to add this user to the chat?",
        )
        .then(() => {
          if (isNewThread) {
            createThread((newThread) => {
              const newParticipants = [...newThread.participants, newId];
              api.messages.updateThread(
                newThread._id,
                { participants: newParticipants },
                (newThread) => {
                  onMessageThreadSelected(newThread.thread);
                },
                (err) => toast.error(err.message),
              );
            });
          } else {
            const newParticipants = [...participants, newId];
            api.messages.updateThread(
              threadId,
              { participants: newParticipants },
              (thread) => {
                onMessageThreadSelected(thread.thread);
              },
              (err) => toast.error(err.message),
            );
          }
        })
        .catch(() => {});
    },
    [
      onMessageThreadSelected,
      createThread,
      isNewThread,
      participants,
      threadId,
    ],
  );

  const searchMessageRecipient = useCallback(
    (e: MessageInputChangeEvent) => {
      setSearchState((prevState) => ({
        ...prevState,
        searching: true,
        searchValue: e?.target?.value,
      }));
      api.search.messageRecipient(
        e?.target?.value || "",
        ({ users }) => {
          const filteredUsers = users.filter(
            (u) => !participants.includes(u._id),
          );
          setSearchState((prevState) => ({
            ...prevState,
            searching: false,
            results: filteredUsers,
          }));
        },
        (err) => {
          toast.error(err.message);
          setSearchState((prevState) => ({ ...prevState, searching: false }));
        },
      );
    },
    [participants],
  );

  const leaveThread = useCallback(
    () =>
      util
        .confirm(
          "Leave chat",
          "Leaving this chat means you will not receive any more messages and will lose access to its messages. Do you really want to leave this chat?",
        )
        .then(() => {
          api.messages.leaveThread(
            threadId,
            () => {
              onMessageThreadSelected(null);
              onCloseModal();
              closeThread(threadId);
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {}),
    [threadId, onMessageThreadSelected, closeThread, onCloseModal],
  );

  const handleSearchChange = useCallback(
    (e: MessageInputChangeEvent) => {
      e.persist();
      searchMessageRecipient(e);
    },
    [searchMessageRecipient],
  );

  const muteThread = useCallback(() => {
    api.messages.muteThread(
      threadId,
      !isMuted,
      ({ participantsMuted: newParticipantsMuted }) => {
        onMessageThreadSelected({
          ...thread,
          participantsMuted: newParticipantsMuted,
        });
      },
      (err) => toast.error(err.message),
    );
  }, [threadId, isMuted, onMessageThreadSelected, thread]);

  return (
    <Modal
      open={modalOpen}
      onClose={onCloseModal}
      style={{ minHeight: "50vh", maxHeight: "90vh" }}
    >
      <ModalContent>
        <H3 style={{ marginBottom: 5 }}>Thread settings</H3>
        <Segment className={modalContainerClass}>
          <ActionArea>
            <H4>Participants</H4>
            <ParticipantContainer>
              {participantUsers.map((u) => (
                <Participant key={u._id}>
                  <UserChip user={u} compact />
                </Participant>
              ))}
            </ParticipantContainer>
          </ActionArea>
          <ActionArea>
            <H4>Add participant</H4>
            <ParticipantContainer>
              <Input
                size="small"
                fluid
                onChange={(e) => handleSearchChange(e)}
                placeholder={t("messages.findRecipient")}
              />
              {results.slice(0, 5).map((u) => (
                <Participant key={u._id}>
                  <UserChip user={u} />
                  {[thread.user, ...participants].indexOf(u._id) > -1 ? (
                    <div />
                  ) : (
                    <Button onClick={() => addParticipant(u._id)} size="small">
                      <Icon icon={PlusIcon} />
                    </Button>
                  )}
                </Participant>
              ))}
            </ParticipantContainer>
          </ActionArea>
        </Segment>
        {!isNewThread ? (
          <div>
            <Segment className={modalContainerClass}>
              <ActionArea>
                <H4>Mute chat</H4>
                <span>
                  You will no longer receive push notifications or emails when
                  messages are sent to this chat.
                </span>
                <Button
                  color={isMuted ? "blue" : undefined}
                  onClick={() => muteThread()}
                  style={{ width: "fit-content", marginTop: 10 }}
                >
                  {isMuted ? "Un-mute chat" : "Mute chat"}
                </Button>
              </ActionArea>
            </Segment>
            <Segment className={cx(modalContainerClass, "is-margined-top")}>
              <ActionArea>
                <H4>Leave chat</H4>
                If you leave this chat, you will no longer receive messages from
                it.
                <Button
                  style={{ maxWidth: "fit-content", marginTop: 10 }}
                  size="small"
                  onClick={() => leaveThread()}
                  variant="outline"
                >
                  <Icon icon={LogOutIcon} />
                  Leave chat
                </Button>
              </ActionArea>
            </Segment>
          </div>
        ) : null}
        {thread?.forType === "idea" ? (
          <Message color="blue">
            The participants of this chat are tied to the {t("generic.idea")}{" "}
            team. If the {t("generic.idea")} is deleted, you will continue to be
            able to use the chat, along with edit the participants.
          </Message>
        ) : null}
      </ModalContent>
    </Modal>
  );
};

export default ChatSettings;
